import {
  SOCKET_ACTIONS,
  MESSAGE_TYPES,
  WebRTCOnNewPeerCB,
  WebRTCActionDataType
} from '@medicusunion/front-utils';
import { v4 as uuid } from 'uuid';
import socket from 'services/socket';
import webrtc from 'services/webrtc';
import { USER_ROLES } from 'constants/users';
import { createRef } from 'react';

import { instance } from 'utils/api';
import {
  audioCallContentRef,
  audioCallRemoteVideoRef
} from 'components/shared/AudioCall/_refs';
import { tokenStorage } from 'utils/storage';

export const audioCallLocalStream = createRef<MediaStream | null>();

class Voip {
  data: Record<string, string>;
  constructor() {
    this.data = {};
  }

  startCall = async (userID: string) => {
    const callUUID = uuid() as string;

    try {
      const res = await instance.get('call-notification', {
        params: {
          userId: userID,
          role: USER_ROLES.patient,
          callUUID
        }
      });

      const roomID = res?.data?.data?.roomId;
      const callerName = res?.data?.data?.callerName;

      if (!roomID) {
        throw new Error();
      }

      this.data = {
        callerName,
        roomID,
        userID,
        callUUID
      };

      socket.connect(this.data.roomID, tokenStorage.get());

      socket.io.on(SOCKET_ACTIONS.CONNECT, async () => {
        const isVideoEnabled = false;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        audioCallLocalStream.current = await webrtc.getUserMedia({
          isVideoEnabled,
          isAudioEnabled: true
        });

        if (!audioCallLocalStream.current) {
          return;
        }

        socket.sendToPeer(SOCKET_ACTIONS.JOIN, {
          audioEnabled: isVideoEnabled,
          videoEnabled: true
        });

        const handleNewPeer = (data: WebRTCOnNewPeerCB) => {
          audioCallContentRef.current?.connecting();

          webrtc.onNewPeer({
            localStream: audioCallLocalStream.current,
            ontrack(params) {
              if (audioCallRemoteVideoRef.current) {
                audioCallRemoteVideoRef.current.srcObject = params.stream;
              }
            },
            onConnect() {
              audioCallContentRef.current?.connect();
            }
          })(data);
        };

        socket.io.on(SOCKET_ACTIONS.ADD_PEER, handleNewPeer);

        const handleSessionDescription = webrtc.onSessionDescription({});

        socket.io.on(
          SOCKET_ACTIONS.SESSION_DESCRIPTION,
          handleSessionDescription
        );

        socket.io.on(SOCKET_ACTIONS.ICE_CANDIDATE, webrtc.onIceCandidate);

        const handleSwitchAction = ({ type, value }: WebRTCActionDataType) => {
          if (type === MESSAGE_TYPES.video) {
            audioCallContentRef.current?.setRemoteVideoEnabled(value);
          }
        };

        socket.io.on(SOCKET_ACTIONS.SWITCH_ACTION, handleSwitchAction);

        const handleRemovePeer = ({ peerID }: { peerID: string }) => {
          webrtc.onRemovePeer(peerID);
          this.endCall();
        };

        socket.io.on(SOCKET_ACTIONS.REMOVE_PEER, handleRemovePeer);
      });

      socket.io.on('error', console.error);
      socket.io.on('connect_error', console.error);

      setTimeout(() => {
        audioCallContentRef.current?.call(callerName);
      }, 500);
    } catch {
      return null;
    }
  };

  endCall = () => {
    audioCallLocalStream.current?.getTracks().forEach(track => track.stop());

    if (socket.socket) {
      socket.sendToPeer(SOCKET_ACTIONS.LEAVE);
      socket.io.disconnect();
    }

    audioCallContentRef.current?.end();
  };
}

const voip = new Voip();

export default voip;
